import { Button, Group } from "@mantine/core";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useCallback } from "react";

// Phone formatting constants
const MIN_PHONE_DIGITS = 10;
const COUNTRY_CODE_WITH_AREA_LENGTH = 12;
const LANDLINE_DIGITS = 8;
const MOBILE_DIGITS = 9;
const LANDLINE_WITHOUT_COUNTRY = 10;
const MOBILE_WITHOUT_COUNTRY = 11;
const COUNTRY_CODE_START = 2;
const AREA_CODE_END = 4;
const LANDLINE_SPLIT_POSITION = 4;
const MOBILE_SPLIT_POSITION = 5;
const AREA_CODE_START = 0;
const AREA_CODE_LENGTH = 2;
const PHONE_NUMBER_START = 2;
const BRAZIL_COUNTRY_CODE = "55";

const formatPhoneNumber = (telefone: string): string => {
  if (!telefone) return "";

  // Remove todos os caracteres que não são dígitos
  const digitsOnly = telefone.replaceAll(/\D/g, "");

  // Se não tem dígitos suficientes, retorna como está
  if (digitsOnly.length < MIN_PHONE_DIGITS) return telefone;

  // Se já tem código do país 55, aplica formatação
  if (digitsOnly.startsWith(BRAZIL_COUNTRY_CODE) && digitsOnly.length >= COUNTRY_CODE_WITH_AREA_LENGTH) {
    const areaCode = digitsOnly.slice(COUNTRY_CODE_START, AREA_CODE_END);
    const phoneNumber = digitsOnly.slice(AREA_CODE_END);

    if (phoneNumber.length === LANDLINE_DIGITS) {
      // Telefone fixo: 55-(xx) xxxx-xxxx
      return `${BRAZIL_COUNTRY_CODE}-(${areaCode}) ${phoneNumber.slice(0, LANDLINE_SPLIT_POSITION)}-${phoneNumber.slice(LANDLINE_SPLIT_POSITION)}`;
    } else if (phoneNumber.length === MOBILE_DIGITS) {
      // Celular: 55-(xx) 9xxxx-xxxx
      return `${BRAZIL_COUNTRY_CODE}-(${areaCode}) ${phoneNumber.slice(0, MOBILE_SPLIT_POSITION)}-${phoneNumber.slice(MOBILE_SPLIT_POSITION)}`;
    }
  }

  // Se não tem código do país, adiciona 55 e formata
  if (digitsOnly.length === LANDLINE_WITHOUT_COUNTRY) {
    // Telefone fixo sem código do país
    const areaCode = digitsOnly.slice(AREA_CODE_START, AREA_CODE_LENGTH);
    const phoneNumber = digitsOnly.slice(PHONE_NUMBER_START);
    return `${BRAZIL_COUNTRY_CODE}-(${areaCode}) ${phoneNumber.slice(0, LANDLINE_SPLIT_POSITION)}-${phoneNumber.slice(LANDLINE_SPLIT_POSITION)}`;
  } else if (digitsOnly.length === MOBILE_WITHOUT_COUNTRY) {
    // Celular sem código do país
    const areaCode = digitsOnly.slice(AREA_CODE_START, AREA_CODE_LENGTH);
    const phoneNumber = digitsOnly.slice(PHONE_NUMBER_START);
    return `${BRAZIL_COUNTRY_CODE}-(${areaCode}) ${phoneNumber.slice(0, MOBILE_SPLIT_POSITION)}-${phoneNumber.slice(MOBILE_SPLIT_POSITION)}`;
  }

  // Se não conseguir formatar, retorna original
  return telefone;
};

interface Habilitada {
  habilitada: boolean;
}

/* eslint-disable @typescript-eslint/no-floating-promises */
// prettier-ignore
const ExportExcel = ({ newEmpresa }: { newEmpresa: (TEmpresa & Habilitada)[] }) => {

  const handleExport = useCallback(async () => {
    const formatEmpresa = newEmpresa?.map((item) => {
      return [
        item.nomeFantasia,
        item.codigoCNAE,
        item.cnpj,
        item.responsavel.nome,
        item.responsavel.email,
        formatPhoneNumber(item.telefone),
        item.porte,
        item.endereco.estado,
        item.categorias,
        new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(new Date(item.createdAt))
      ]
    })
    await onExport(formatEmpresa);
  }, [newEmpresa]);

  return <Group justify={"flex-end"}>
    {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
    <Button variant="filled" onClick={handleExport}>Exportar</Button>
  </Group>

};

export default ExportExcel;

/* eslint-disable sonarjs/cognitive-complexity */
// prettier-ignore
const onExport = async (csvData: (string | number | undefined)[][] | undefined) => {

  // Se csvData for undefined, interrompa a execução
  if (!csvData) return;

  const columns = ["Empresa", "Código CNAE", "Cnpj", "Responsavel", "Email", "Telefone", "Porte", "Estado", "Categorias", "Data Cadastro"];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Empresas');

  const headerRow = worksheet.addRow(columns);

  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });

  if (Array.isArray(csvData)) {
    for (const rowData of csvData) {
      worksheet.addRow(Object.values(rowData)); // Removi a assertiva de tipo desnecessária
    }
  }

  try { // Adicionei um bloco try para capturar possíveis erros
    const buffer = await workbook.xlsx.writeBuffer(); // Aguardo a resolução da Promise
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `relatorio.xlsx`
    );
  } catch (error) { // Tratamento de erro
    console.error("Erro ao escrever buffer:", error);
  }
}
